import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ShieldCheck, Eye, EyeOff, Home, Fingerprint, Loader2 } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../utils/api';
import { showErrorToast } from '../components/ErrorDisplay';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  checkBiometricAvailable,
  authenticateWithBiometric,
  registerBiometric,
} from '../utils/webauthn';

const Login = () => {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricUserId, setBiometricUserId] = useState(null);
  const [platformAuthAvailable, setPlatformAuthAvailable] = useState(false);
  const recaptchaRef = useRef(null);

  // Check for saved credentials and biometric availability
  useEffect(() => {
    // Load saved credentials
    const savedEmail = localStorage.getItem('ke_saved_email');
    const savedRememberMe = localStorage.getItem('ke_remember_me') === 'true';
    if (savedEmail && savedRememberMe) {
      setEmail(savedEmail);
      setRememberMe(true);
      // Check if this user has biometric registered
      checkUserBiometric(savedEmail);
    }

    // Check platform authenticator availability
    checkPlatformAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check when email changes
  useEffect(() => {
    if (email && email.length > 2) {
      const timeoutId = setTimeout(() => {
        checkUserBiometric(email);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setBiometricAvailable(false);
      setBiometricUserId(null);
    }
  }, [email]);

  const checkPlatformAuth = async () => {
    const available = await isPlatformAuthenticatorAvailable();
    setPlatformAuthAvailable(available);
  };

  const checkUserBiometric = async (username) => {
    if (!isWebAuthnSupported()) return;
    
    const result = await checkBiometricAvailable(username);
    setBiometricAvailable(result.available);
    setBiometricUserId(result.user_id);
  };

  const handleBiometricLogin = async () => {
    if (!biometricUserId) {
      toast.error('لم يتم تسجيل بصمة لهذا الحساب');
      return;
    }

    setBiometricLoading(true);
    try {
      const result = await authenticateWithBiometric(biometricUserId);
      
      if (result.success) {
        // Store token and user data
        localStorage.setItem('token', result.data.access_token);
        setUser(result.data.user);
        
        toast.success(result.data.message || 'تم تسجيل الدخول بالبصمة');
        
        // Navigate based on role
        if (result.data.user.role === 'guest') {
          navigate('/booking');
        } else {
          navigate('/dashboard');
        }
      } else {
        toast.error(result.error || 'فشل التحقق من البصمة');
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      showErrorToast(error, 'خطأ في تسجيل الدخول بالبصمة', 'login-biometric');
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require reCAPTCHA
    if (!recaptchaToken) {
      toast.error(language === 'ar' ? 'يرجى إكمال التحقق الأمني' : 'Please complete the security verification');
      return;
    }

    setLoading(true);

    try {
      // Verify reCAPTCHA
      if (recaptchaToken) {
        try {
          await api.post('/auth/verify-recaptcha', { token: recaptchaToken });
        } catch (e) {
          // If reCAPTCHA verification fails, continue anyway (for dev environments)
          console.log('reCAPTCHA verification skipped');
        }
      }
      
      // Then login (backend accepts both email and username)
      const user = await login(email, password);
      
      // Handle Remember Me
      if (rememberMe) {
        localStorage.setItem('ke_saved_email', email);
        localStorage.setItem('ke_remember_me', 'true');
        // Setup biometric if available
        if (biometricAvailable) {
          try { await registerBiometric(biometricUserId); }
          catch (err) { console.debug('[Login] biometric enrolment skipped:', err?.message); }
        }
      } else {
        localStorage.removeItem('ke_saved_email');
        localStorage.removeItem('ke_remember_me');
        localStorage.removeItem('ke_biometric_credential');
        localStorage.removeItem('ke_saved_pass');
      }
      
      toast.success(t('welcome'));
      
      // Redirect based on role
      if (user.role === 'guest') {
        navigate('/booking');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل تسجيل الدخول' : 'Login failed'));
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const hasSavedCredentials = localStorage.getItem('ke_saved_email') && localStorage.getItem('ke_biometric_credential');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center relative">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1622622291585-8d45fc593a55?q=80&w=1920&auto=format&fit=crop')] bg-cover bg-center opacity-10"></div>
      
      {/* Top Navigation Buttons */}
      <div className="absolute top-6 start-6 flex items-center gap-3">
        <Button
          data-testid="home-btn"
          onClick={() => navigate('/')}
          className="bg-primary/20 backdrop-blur-sm border border-primary/30 hover:bg-primary/30"
          size="sm"
        >
          <Home className="w-4 h-4 me-2" />
          {language === 'ar' ? 'الرئيسية' : 'Home'}
        </Button>
        
        {/* Current Language Flag - Large (2x size) */}
        <div 
          className="flex items-center gap-2 px-2 py-1 bg-primary/10 backdrop-blur-sm border border-primary/20 rounded-lg"
          title={language === 'ar' ? 'اللغة الحالية: العربية' : 'Current Language: English'}
        >
          {language === 'ar' ? (
            <img 
              src="https://flagcdn.com/w80/ae.png" 
              alt="العربية" 
              className="w-10 h-7 object-cover rounded shadow-md border border-white/10"
            />
          ) : (
            <img 
              src="https://flagcdn.com/w80/us.png" 
              alt="English" 
              className="w-10 h-7 object-cover rounded shadow-md border border-white/10"
            />
          )}
        </div>
      </div>
      
      {/* Language Toggle Button */}
      <Button
        data-testid="language-toggle-btn"
        onClick={toggleLanguage}
        className="absolute top-6 end-6 bg-secondary/50 backdrop-blur-sm border border-white/10 flex items-center gap-2"
        size="sm"
        title={language === 'ar' ? 'Switch to English' : 'التبديل للعربية'}
      >
        {language === 'ar' ? (
          <>
            <img 
              src="https://flagcdn.com/w40/us.png" 
              alt="English" 
              className="w-6 h-4 object-cover rounded-sm"
            />
            <span>English</span>
          </>
        ) : (
          <>
            <img 
              src="https://flagcdn.com/w40/ae.png" 
              alt="العربية" 
              className="w-6 h-4 object-cover rounded-sm"
            />
            <span>العربية</span>
          </>
        )}
      </Button>

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-8 backdrop-blur-sm">
          <div className="text-center mb-8">
            <div className="inline-block mb-4">
              <img 
                src="/logo-gold.png" 
                alt="KE Gents Saloon" 
                className="w-28 h-28 object-contain mx-auto"
              />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">KE Gents Saloon</h1>
            <p className="text-muted-foreground">{t('login')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {language === 'ar' ? 'اسم المستخدم أو البريد الإلكتروني' : 'Username or Email'}
              </label>
              <Input
                data-testid="email-input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={language === 'ar' ? 'اسم المستخدم أو البريد الإلكتروني' : 'Username or Email'}
                required
                className="w-full bg-secondary/50 border-white/10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('password')}
              </label>
              <div className="relative">
                <Input
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('password')}
                  required
                  className="w-full bg-secondary/50 border-white/10 pe-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <label 
                  htmlFor="remember-me" 
                  className="text-sm text-muted-foreground cursor-pointer hover:text-foreground"
                >
                  {language === 'ar' ? 'تذكرني' : 'Remember me'}
                </label>
              </div>
            </div>

            {/* Biometric Login Button */}
            {platformAuthAvailable && biometricAvailable && biometricUserId && (
              <Button
                type="button"
                onClick={handleBiometricLogin}
                variant="outline"
                className="w-full border-primary/50 text-primary hover:bg-primary/10"
                disabled={loading || biometricLoading}
                data-testid="biometric-login-btn"
              >
                {biometricLoading ? (
                  <Loader2 className="w-5 h-5 me-2 animate-spin" />
                ) : (
                  <Fingerprint className="w-5 h-5 me-2" />
                )}
                {language === 'ar' ? 'تسجيل الدخول بالبصمة / Face ID' : 'Login with Biometric / Face ID'}
              </Button>
            )}

            {/* Google reCAPTCHA */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <span>{language === 'ar' ? 'التحقق الأمني' : 'Security Verification'}</span>
              </div>
              
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                theme="dark"
                hl={language}
              />
            </div>

            <Button
              data-testid="login-btn"
              type="submit"
              disabled={loading || !recaptchaToken}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all duration-300 font-bold tracking-wide uppercase disabled:opacity-50"
            >
              {loading ? t('loading') || 'جاري التحميل...' : t('login')}
            </Button>

            {/* Biometric Login Button */}
            {platformAuthAvailable && biometricAvailable && biometricUserId && (
              <Button
                data-testid="biometric-login-btn"
                type="button"
                onClick={handleBiometricLogin}
                disabled={biometricLoading}
                variant="outline"
                className="w-full border-primary/30 hover:bg-primary/10 hover:border-primary flex items-center justify-center gap-2"
              >
                {biometricLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary"></div>
                    <span>{language === 'ar' ? 'جاري التحقق...' : 'Verifying...'}</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                      <path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4"/>
                      <path d="M5 19.5C5.5 18 6 15 6 12c0-.7.12-1.37.34-2"/>
                      <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02"/>
                      <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"/>
                      <path d="M8.65 22c.21-.66.45-1.32.57-2"/>
                      <path d="M14 13.12c0 2.38 0 6.38-1 8.88"/>
                      <path d="M2 16h.01"/>
                      <path d="M21.8 16c.2-2 .131-5.354 0-6"/>
                      <path d="M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2"/>
                    </svg>
                    <span>{language === 'ar' ? 'الدخول بالبصمة' : 'Login with Biometric'}</span>
                  </>
                )}
              </Button>
            )}

            {/* Show hint to setup biometric */}
            {platformAuthAvailable && !biometricAvailable && email && email.length > 2 && (
              <p className="text-xs text-muted-foreground text-center">
                {language === 'ar' 
                  ? 'يمكنك تفعيل البصمة من صفحة الملف الشخصي بعد تسجيل الدخول'
                  : 'You can enable biometric login from your profile after logging in'}
              </p>
            )}

            <div className="text-center">
              <Link 
                to="/forgot-password" 
                className="text-primary hover:underline text-sm"
                data-testid="forgot-password-link"
              >
                {language === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;